// Persistent per-device id required by the DRM license POST (X-Device-Id).
// havik-drm rejects a missing id (400 missing_device_id) and validates the
// format against ^[a-zA-Z0-9\-_.@:]+$ — a UUID passes. We persist it in
// localStorage so it is stable across reloads, falling back to an in-memory id
// when storage is unavailable (private mode), which is still valid per request.

const STORAGE_KEY = 'havik.player.deviceId';
let cached: string | undefined;

export function getDeviceId(): string {
  if (cached) return cached;
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (stored) {
      cached = stored;
      return stored;
    }
  } catch {
    // storage blocked — fall through to generate
  }
  const id = generateUuid();
  cached = id;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, id);
  } catch {
    // best-effort persistence
  }
  return id;
}

/**
 * Clear the persisted device id (localStorage + in-memory cache). The next
 * getDeviceId() generates a fresh one. Provide this to viewers as a privacy /
 * "reset my device identifier" control — the device id is sent as X-Device-Id
 * on every DRM license request and persists across sessions.
 */
export function clearDeviceId(): void {
  cached = undefined;
  try {
    globalThis.localStorage?.removeItem(STORAGE_KEY);
  } catch {
    // storage unavailable — the in-memory cache is already cleared
  }
}

function generateUuid(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();

  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return (
    hex.slice(0, 4).join('') +
    '-' +
    hex.slice(4, 6).join('') +
    '-' +
    hex.slice(6, 8).join('') +
    '-' +
    hex.slice(8, 10).join('') +
    '-' +
    hex.slice(10, 16).join('')
  );
}
